import * as React from 'react';
import {
  AlertCircle,
  Globe,
  Monitor,
  Clock,
  Search,
  ChevronDown,
  ChevronRight,
  Terminal,
  Wifi,
  MousePointer,
  Navigation,
  AlertTriangle,
  Info,
  Bug,
  Filter,
  X,
  Code,
} from 'lucide-react';
import { cn } from '../lib/utils.js';

/**
 * Render text with newlines as <br/> elements
 */
function renderWithNewlines(text: string): React.ReactNode {
  const parts = text.split('\n');
  if (parts.length === 1) return text;
  return parts.map((part, i) => (
    <React.Fragment key={i}>
      {part}
      {i < parts.length - 1 && <br />}
    </React.Fragment>
  ));
}
import { Input } from './ui/input.js';
import { Badge } from './ui/badge.js';
import { Button } from './ui/button.js';
import { CodeBlock } from './ui/codeBlock.js';

/**
 * Breadcrumb entry from error tracking
 */
interface Breadcrumb {
  type: string;
  category: string;
  message: string;
  level: 'debug' | 'info' | 'warning' | 'error';
  timestamp: string;
  data?: Record<string, unknown>;
}

/**
 * Browser information from error tracking
 */
interface BrowserInfo {
  name?: string;
  version?: string;
  os?: string;
  language?: string;
  cookiesEnabled?: boolean;
  screenWidth?: number;
  screenHeight?: number;
  viewportWidth?: number;
  viewportHeight?: number;
  devicePixelRatio?: number;
  online?: boolean;
}

/**
 * Error context structure from browser error tracking
 */
export interface ErrorContext {
  version?: string;
  environment?: string;
  userAgent?: string;
  errorType?: string;
  message?: string;
  url?: string;
  sessionId?: string;
  browser?: BrowserInfo;
  breadcrumbs?: Breadcrumb[];
  browserBreadcrumbs?: Breadcrumb[]; // Alternative field name used by bug reports
  stack?: string;
  errorStack?: string;
  // Bug report specific fields
  description?: string;
  reporter?: string;
  stepsToReproduce?: string;
  expectedBehavior?: string;
  actualBehavior?: string;
  // Additional bug report fields
  screenWidth?: number;
  screenHeight?: number;
  // Backend log fields
  method?: string;
  path?: string;
  duration?: number;
  status?: number;
  requestId?: string;
  // Backend error fields (e.g., Prisma errors)
  error?: string;
  errorCode?: string;
  model?: string;
  action?: string;
  success?: boolean;
  correlationId?: string;
}

/**
 * Check if context matches error tracking structure (errors or bug reports)
 */
export function isErrorContext(context: Record<string, unknown> | null): boolean {
  if (!context) return false;
  return (
    // Has breadcrumbs array (error tracking or bug report)
    Array.isArray(context['breadcrumbs']) ||
    // Has browserBreadcrumbs array (bug reports)
    Array.isArray(context['browserBreadcrumbs']) ||
    // Automatic error tracking with errorType and message
    (typeof context['errorType'] === 'string' && typeof context['message'] === 'string') ||
    // User-submitted bug report with description
    typeof context['description'] === 'string' ||
    // Backend error (e.g., Prisma) with error field
    (typeof context['error'] === 'string' && context['success'] === false)
  );
}

/**
 * Check if context is a bug report (vs automatic error tracking)
 */
export function isBugReport(context: ErrorContext): boolean {
  return typeof context.description === 'string' && !context.errorType;
}

/**
 * Check if context is an actual error (has error type or message)
 */
export function isActualError(context: ErrorContext): boolean {
  return !!(context.errorType || context.stack || context.errorStack || (context.error && context.success === false));
}

/**
 * Check if context is a backend error (e.g., Prisma)
 */
export function isBackendError(context: ErrorContext): boolean {
  return !!(context.error && context.success === false);
}

/**
 * Get the display title for the context
 */
function getContextTitle(context: ErrorContext): { title: string; isError: boolean; isBugReport: boolean } {
  if (isBugReport(context)) {
    return { title: `Bug Report: ${context.description}`, isError: false, isBugReport: true };
  }
  // Backend error (e.g., Prisma) with model and action
  if (context.error && context.success === false) {
    const prefix = context.errorCode ? `[${context.errorCode}] ` : '';
    if (context.model && context.action) {
      return { title: `${prefix}${context.model}.${context.action} failed`, isError: true, isBugReport: false };
    }
    // Truncate error message if too long
    const errorMsg = context.error.length > 100 ? context.error.slice(0, 100) + '...' : context.error;
    return { title: `${prefix}${errorMsg}`, isError: true, isBugReport: false };
  }
  if (context.errorType && context.message) {
    return { title: `${context.errorType}: ${context.message}`, isError: true, isBugReport: false };
  }
  if (context.errorType) {
    return { title: context.errorType, isError: true, isBugReport: false };
  }
  if (context.message) {
    return { title: context.message, isError: true, isBugReport: false };
  }
  // For logs with breadcrumbs but no error info (like slow requests)
  if (context.method && context.path) {
    return { title: `${context.method} ${context.path}`, isError: false, isBugReport: false };
  }
  return { title: 'Log Details', isError: false, isBugReport: false };
}

interface ErrorContextViewerProps {
  context: ErrorContext;
  className?: string;
  /** Callback when user clicks to filter by session ID */
  onFilterSession?: (sessionId: string) => void;
}

/**
 * Get icon for breadcrumb type
 */
function getBreadcrumbIcon(type: string, category: string) {
  switch (type) {
    case 'http':
      return <Wifi className="h-3.5 w-3.5" />;
    case 'click':
      return <MousePointer className="h-3.5 w-3.5" />;
    case 'navigation':
      return <Navigation className="h-3.5 w-3.5" />;
    case 'error':
      return <AlertCircle className="h-3.5 w-3.5" />;
    case 'console':
      if (category === 'console.error') return <AlertCircle className="h-3.5 w-3.5" />;
      if (category === 'console.warn') return <AlertTriangle className="h-3.5 w-3.5" />;
      return <Terminal className="h-3.5 w-3.5" />;
    default:
      return <Info className="h-3.5 w-3.5" />;
  }
}

/**
 * Get level color classes
 */
function getLevelClasses(level: string) {
  switch (level) {
    case 'error':
      return {
        bg: 'bg-red-500/10',
        border: 'border-red-500/30',
        text: 'text-red-600 dark:text-red-400',
        dot: 'bg-red-500',
      };
    case 'warning':
      return {
        bg: 'bg-amber-500/10',
        border: 'border-amber-500/30',
        text: 'text-amber-600 dark:text-amber-400',
        dot: 'bg-amber-500',
      };
    case 'debug':
      return {
        bg: 'bg-slate-500/10',
        border: 'border-slate-500/30',
        text: 'text-slate-500',
        dot: 'bg-slate-400',
      };
    default: // info
      return {
        bg: 'bg-blue-500/10',
        border: 'border-blue-500/30',
        text: 'text-blue-600 dark:text-blue-400',
        dot: 'bg-blue-500',
      };
  }
}

/**
 * Format relative timestamp
 */
function formatRelativeTime(timestamp: string, referenceTime?: Date): string {
  const date = new Date(timestamp);
  const ref = referenceTime ?? new Date();
  const diffMs = ref.getTime() - date.getTime();

  if (diffMs < 1000) return 'just now';
  if (diffMs < 60000) return `${Math.floor(diffMs / 1000)}s ago`;
  if (diffMs < 3600000) return `${Math.floor(diffMs / 60000)}m ago`;
  if (diffMs < 86400000) return `${Math.floor(diffMs / 3600000)}h ago`;
  return date.toLocaleString();
}

/**
 * Format timestamp for display
 */
function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    fractionalSecondDigits: 3,
    hour12: false,
  });
}

/**
 * Get HTTP status badge variant
 */
function getHttpStatusVariant(status: number): 'debug' | 'info' | 'warn' | 'error' {
  if (status >= 500) return 'error';
  if (status >= 400) return 'warn';
  if (status >= 300) return 'info';
  return 'debug';
}

/**
 * Truncate long messages
 */
function truncateMessage(message: string, maxLength = 150): { text: string; truncated: boolean } {
  if (message.length <= maxLength) return { text: message, truncated: false };
  return { text: message.slice(0, maxLength) + '...', truncated: true };
}

/**
 * Parse and format JSON in message if present
 */
function parseMessageJson(message: string): { prefix: string; json: Record<string, unknown> | null } {
  const jsonMatch = message.match(/^([^{]*?)(\{.+\})$/s);
  if (jsonMatch && jsonMatch[1] !== undefined && jsonMatch[2] !== undefined) {
    try {
      const json = JSON.parse(jsonMatch[2]) as Record<string, unknown>;
      return { prefix: jsonMatch[1].trim(), json };
    } catch {
      return { prefix: message, json: null };
    }
  }
  return { prefix: message, json: null };
}

/**
 * Safely get HTTP data from breadcrumb
 */
function getHttpData(data: Record<string, unknown> | undefined): {
  status?: number;
  duration?: number;
  url?: string;
  method?: string;
} | null {
  if (!data) return null;
  return {
    status: typeof data['status'] === 'number' ? data['status'] : undefined,
    duration: typeof data['duration'] === 'number' ? data['duration'] : undefined,
    url: typeof data['url'] === 'string' ? data['url'] : undefined,
    method: typeof data['method'] === 'string' ? data['method'] : undefined,
  };
}

/**
 * BreadcrumbItem component
 */
function BreadcrumbItem({
  breadcrumb,
  rawBreadcrumb,
  isLast,
  searchTerm,
}: {
  breadcrumb: Breadcrumb;
  rawBreadcrumb: Record<string, unknown>;
  isLast: boolean;
  searchTerm: string;
}) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [showRaw, setShowRaw] = React.useState(false);
  const levelClasses = getLevelClasses(breadcrumb.level);
  const { text: truncatedMessage, truncated } = truncateMessage(breadcrumb.message);
  const { json } = parseMessageJson(breadcrumb.message);

  const hasExpandableContent = truncated || json || breadcrumb.data;
  const isHttpRequest = breadcrumb.type === 'http' && breadcrumb.data;
  const httpData = getHttpData(breadcrumb.data);

  // Highlight search matches and render newlines
  const highlightMatch = (text: string): React.ReactNode => {
    // Process newlines first
    const lines = text.split('\n');
    const processedLines = lines.map((line, lineIndex) => {
      // Apply search highlighting to each line
      if (!searchTerm) {
        return line;
      }
      const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      const parts = line.split(regex);
      return parts.map((part, partIndex) =>
        regex.test(part) ? (
          <mark key={`${lineIndex}-${partIndex}`} className="bg-yellow-300 dark:bg-yellow-600 rounded px-0.5">{part}</mark>
        ) : part
      );
    });

    // Join lines with <br /> elements
    if (lines.length === 1) {
      return processedLines[0];
    }
    return processedLines.map((line, i) => (
      <React.Fragment key={i}>
        {line}
        {i < processedLines.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  return (
    <div className="relative pl-6">
      {/* Timeline line */}
      {!isLast && (
        <div className="absolute left-[7px] top-6 bottom-0 w-px bg-[var(--color-border)]" />
      )}

      {/* Timeline dot */}
      <div className={cn(
        'absolute left-0 top-1.5 h-3.5 w-3.5 rounded-full border-2 border-[var(--color-background)]',
        levelClasses.dot
      )} />

      <div className={cn(
        'rounded-lg border p-2.5 mb-2',
        levelClasses.bg,
        levelClasses.border,
      )}>
        {/* Header row */}
        <div className="flex items-start gap-2">
          {/* Icon */}
          <span className={cn('mt-0.5 shrink-0', levelClasses.text)}>
            {getBreadcrumbIcon(breadcrumb.type, breadcrumb.category)}
          </span>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Type and timestamp */}
            <div className="flex items-center gap-2 mb-1">
              <span className={cn('text-xs font-medium', levelClasses.text)}>
                {breadcrumb.category || breadcrumb.type}
              </span>
              <span className="text-xs text-[var(--color-muted-foreground)]">
                {formatTimestamp(breadcrumb.timestamp)}
              </span>
              {isHttpRequest && httpData?.status && (
                <>
                  <Badge variant={getHttpStatusVariant(httpData.status)} className="text-[10px] px-1.5 py-0">
                    {httpData.status}
                  </Badge>
                  {httpData.duration && (
                    <span className="text-xs text-[var(--color-muted-foreground)]">
                      {httpData.duration}ms
                    </span>
                  )}
                </>
              )}
              {/* RAW button */}
              <button
                onClick={(e) => { e.stopPropagation(); setShowRaw(!showRaw); }}
                className={cn(
                  'ml-auto px-1.5 py-0.5 text-[10px] font-medium rounded border transition-colors',
                  showRaw
                    ? 'bg-[var(--color-primary)] text-[var(--color-primary-foreground)] border-[var(--color-primary)]'
                    : 'bg-transparent text-[var(--color-muted-foreground)] border-[var(--color-border)] hover:bg-[var(--color-muted)]'
                )}
                title="Show raw JSON"
              >
                <Code className="h-3 w-3 inline mr-0.5" />
                RAW
              </button>
            </div>

            {/* Message */}
            <div className="text-sm text-[var(--color-foreground)]">
              {hasExpandableContent ? (
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="flex items-start gap-1 text-left hover:underline w-full"
                >
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 shrink-0 mt-0.5" />
                  ) : (
                    <ChevronRight className="h-4 w-4 shrink-0 mt-0.5" />
                  )}
                  <span className={cn('break-all whitespace-pre-wrap', !isExpanded && 'line-clamp-2')}>
                    {highlightMatch(isExpanded ? breadcrumb.message : truncatedMessage)}
                  </span>
                </button>
              ) : (
                <span className="break-all whitespace-pre-wrap">{highlightMatch(breadcrumb.message)}</span>
              )}
            </div>

            {/* Expanded content */}
            {isExpanded && (json || breadcrumb.data) && !showRaw && (
              <div className="mt-2 space-y-2">
                {json && (
                  <CodeBlock maxHeight="max-h-40">{JSON.stringify(json, null, 2)}</CodeBlock>
                )}
                {breadcrumb.data && !json && (
                  <CodeBlock maxHeight="max-h-40">{JSON.stringify(breadcrumb.data, null, 2)}</CodeBlock>
                )}
              </div>
            )}

            {/* RAW JSON view */}
            {showRaw && (
              <div className="mt-2">
                <CodeBlock maxHeight="max-h-60" className="bg-slate-900 border-slate-700 text-slate-300">
                  {JSON.stringify(rawBreadcrumb, null, 2)}
                </CodeBlock>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * ErrorContextViewer renders error tracking context with breadcrumbs timeline
 */
export function ErrorContextViewer({ context, className, onFilterSession }: ErrorContextViewerProps) {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [levelFilter, setLevelFilter] = React.useState<string[]>([]);
  const [typeFilter, setTypeFilter] = React.useState<string[]>([]);
  const [showFilters, setShowFilters] = React.useState(false);

  // Check both breadcrumbs and browserBreadcrumbs (bug reports use browserBreadcrumbs)
  const rawBreadcrumbsArray = (context.breadcrumbs?.length ? context.breadcrumbs : context.browserBreadcrumbs) ?? [];

  // Normalize breadcrumbs - handle both browser and backend formats
  // Browser format: { type, category, message, level, timestamp, data }
  // Backend format: { ts, level, message, sub_app, context }
  // Keep both normalized and raw versions together
  // Cast to unknown[] first to handle mixed format data from backend
  // Filter out non-object items (e.g., truncation markers like "[256 more items]")
  const breadcrumbsWithRaw = (rawBreadcrumbsArray as unknown[])
    .filter((item): item is Record<string, unknown> =>
      item !== null && typeof item === 'object' && !Array.isArray(item)
    )
    .map((b) => {
      // Detect backend format by presence of 'ts' field
      const isBackendFormat = 'ts' in b && !('timestamp' in b);

      const normalized: Breadcrumb = {
        type: (b['type'] as string) || (isBackendFormat ? 'http' : 'info'),
        category: (b['category'] as string) || (b['sub_app'] as string) || (b['type'] as string) || 'log',
        message: (b['message'] as string) || '',
        level: ((b['level'] as string) || 'info') as Breadcrumb['level'],
        timestamp: (b['timestamp'] as string) || (b['ts'] as string) || new Date().toISOString(),
        data: (b['data'] as Record<string, unknown>) || (b['context'] as Record<string, unknown>),
      };

      return { normalized, raw: b };
    });

  const breadcrumbs = breadcrumbsWithRaw.map(b => b.normalized);

  const isBugReportContext = isBugReport(context);

  // Get unique types and levels for filters
  const uniqueTypes = React.useMemo(() =>
    [...new Set(breadcrumbs.map(b => b.type))],
    [breadcrumbs]
  );
  const uniqueLevels = React.useMemo(() =>
    [...new Set(breadcrumbs.map(b => b.level))],
    [breadcrumbs]
  );

  // Filter breadcrumbs (keep both normalized and raw)
  const filteredBreadcrumbsWithRaw = React.useMemo(() => {
    return breadcrumbsWithRaw.filter(({ normalized: b }) => {
      // Search filter
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const matchesMessage = b.message.toLowerCase().includes(term);
        const matchesCategory = b.category.toLowerCase().includes(term);
        const matchesType = b.type.toLowerCase().includes(term);
        if (!matchesMessage && !matchesCategory && !matchesType) return false;
      }

      // Level filter
      if (levelFilter.length > 0 && !levelFilter.includes(b.level)) return false;

      // Type filter
      if (typeFilter.length > 0 && !typeFilter.includes(b.type)) return false;

      return true;
    });
  }, [breadcrumbsWithRaw, searchTerm, levelFilter, typeFilter]);

  const filteredBreadcrumbs = filteredBreadcrumbsWithRaw.map(b => b.normalized);

  // Count by level for summary
  const levelCounts = React.useMemo(() => {
    const counts = { error: 0, warning: 0, info: 0, debug: 0 };
    breadcrumbs.forEach(b => {
      if (b.level in counts) {
        counts[b.level as keyof typeof counts]++;
      }
    });
    return counts;
  }, [breadcrumbs]);

  const toggleLevelFilter = (level: string) => {
    setLevelFilter(prev =>
      prev.includes(level) ? prev.filter(l => l !== level) : [...prev, level]
    );
  };

  const toggleTypeFilter = (type: string) => {
    setTypeFilter(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  const clearFilters = () => {
    setSearchTerm('');
    setLevelFilter([]);
    setTypeFilter([]);
  };

  const hasActiveFilters = searchTerm || levelFilter.length > 0 || typeFilter.length > 0;

  const contextInfo = getContextTitle(context);

  // Determine styling based on context type
  const getHeaderStyles = () => {
    if (contextInfo.isBugReport) {
      return {
        border: 'border-purple-500/30 bg-purple-500/5',
        iconBg: 'bg-purple-500/20',
        iconColor: 'text-purple-500',
        titleColor: 'text-purple-600 dark:text-purple-400',
      };
    }
    if (contextInfo.isError) {
      return {
        border: 'border-red-500/30 bg-red-500/5',
        iconBg: 'bg-red-500/20',
        iconColor: 'text-red-500',
        titleColor: 'text-red-600 dark:text-red-400',
      };
    }
    // Non-error logs with breadcrumbs (like slow requests)
    return {
      border: 'border-blue-500/30 bg-blue-500/5',
      iconBg: 'bg-blue-500/20',
      iconColor: 'text-blue-500',
      titleColor: 'text-blue-600 dark:text-blue-400',
    };
  };

  const headerStyles = getHeaderStyles();

  return (
    <div className={cn('space-y-4', className)}>
      {/* Context Summary Header */}
      <div className={cn('rounded-lg border p-4 overflow-hidden', headerStyles.border)}>
        <div className="flex items-start gap-3">
          <div className={cn('rounded-lg p-2 shrink-0', headerStyles.iconBg)}>
            <Bug className={cn('h-5 w-5', headerStyles.iconColor)} />
          </div>
          <div className="flex-1 min-w-0 overflow-hidden">
            <div className="flex items-start justify-between gap-2 mb-1">
              <h3 className={cn('font-semibold break-all min-w-0', headerStyles.titleColor)}>
                {contextInfo.title}
              </h3>
              {context.sessionId && onFilterSession && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onFilterSession(context.sessionId!)}
                  className="shrink-0 text-xs h-7"
                >
                  <Filter className="w-3 h-3 mr-1.5" />
                  View Session Logs
                </Button>
              )}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--color-muted-foreground)]">
              {context.reporter && (
                <span className="flex items-center gap-1">
                  <span className="opacity-50">reported by:</span>
                  <span className="font-medium">{context.reporter}</span>
                </span>
              )}
              {context.url && (
                <span className="flex items-center gap-1">
                  <Globe className="h-3 w-3" />
                  <span className="font-mono truncate max-w-xs">{context.url}</span>
                </span>
              )}
              {context.environment && (
                <span className="flex items-center gap-1">
                  <span className="opacity-50">env:</span>
                  <Badge variant="info" className="text-[10px] px-1.5 py-0">
                    {context.environment}
                  </Badge>
                </span>
              )}
              {context.version && (
                <span className="flex items-center gap-1">
                  <span className="opacity-50">v{context.version}</span>
                </span>
              )}
              {/* Backend log specific fields */}
              {context.duration && (
                <span className="flex items-center gap-1">
                  <span className="opacity-50">duration:</span>
                  <span className="font-medium">{context.duration}ms</span>
                </span>
              )}
              {context.status && (
                <span className="flex items-center gap-1">
                  <Badge variant={context.status >= 400 ? 'error' : 'debug'} className="text-[10px] px-1.5 py-0">
                    {context.status}
                  </Badge>
                </span>
              )}
              {context.requestId && (
                <span className="flex items-center gap-1">
                  <span className="opacity-50">req:</span>
                  <span className="font-mono text-[10px]">{context.requestId.slice(0, 8)}</span>
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Bug Report Details */}
        {contextInfo.isBugReport && (context.stepsToReproduce || context.expectedBehavior || context.actualBehavior) && (
          <div className="mt-3 pt-3 border-t space-y-2 text-sm border-purple-500/20">
            {context.stepsToReproduce && (
              <div>
                <span className="font-medium text-purple-600 dark:text-purple-400">Steps to Reproduce:</span>
                <p className="mt-1 text-[var(--color-muted-foreground)] whitespace-pre-wrap">{context.stepsToReproduce}</p>
              </div>
            )}
            {context.expectedBehavior && (
              <div>
                <span className="font-medium text-purple-600 dark:text-purple-400">Expected:</span>
                <p className="mt-1 text-[var(--color-muted-foreground)]">{context.expectedBehavior}</p>
              </div>
            )}
            {context.actualBehavior && (
              <div>
                <span className="font-medium text-purple-600 dark:text-purple-400">Actual:</span>
                <p className="mt-1 text-[var(--color-muted-foreground)]">{context.actualBehavior}</p>
              </div>
            )}
          </div>
        )}

        {/* Browser Info */}
        {context.browser && (
          <div className={cn(
            'mt-3 pt-3 border-t',
            contextInfo.isBugReport ? 'border-purple-500/20' : contextInfo.isError ? 'border-red-500/20' : 'border-blue-500/20'
          )}>
            <div className="flex items-center gap-2 text-xs text-[var(--color-muted-foreground)]">
              <Monitor className="h-3.5 w-3.5" />
              <span>{context.browser.name} {context.browser.version}</span>
              <span className="opacity-30">|</span>
              <span>{context.browser.os}</span>
              <span className="opacity-30">|</span>
              <span>{context.browser.viewportWidth}x{context.browser.viewportHeight}</span>
              {context.browser.devicePixelRatio && context.browser.devicePixelRatio > 1 && (
                <>
                  <span className="opacity-30">|</span>
                  <span>@{context.browser.devicePixelRatio}x</span>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Breadcrumbs Timeline */}
      {breadcrumbs.length > 0 && (
        <div className="space-y-3">
          {/* Timeline Header with Search and Filters */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-[var(--color-muted-foreground)]" />
              <h4 className="text-sm font-medium">
                Breadcrumbs Timeline
                <span className="ml-2 text-xs font-normal text-[var(--color-muted-foreground)]">
                  ({filteredBreadcrumbs.length}{hasActiveFilters ? ` of ${breadcrumbs.length}` : ''})
                </span>
              </h4>
            </div>

            {/* Level summary badges */}
            <div className="flex items-center gap-1">
              {levelCounts.error > 0 && (
                <Badge
                  variant="error"
                  className={cn(
                    'text-[10px] px-1.5 py-0 cursor-pointer',
                    levelFilter.includes('error') && 'ring-2 ring-red-500'
                  )}
                  onClick={() => toggleLevelFilter('error')}
                >
                  {levelCounts.error} errors
                </Badge>
              )}
              {levelCounts.warning > 0 && (
                <Badge
                  variant="warn"
                  className={cn(
                    'text-[10px] px-1.5 py-0 cursor-pointer',
                    levelFilter.includes('warning') && 'ring-2 ring-amber-500'
                  )}
                  onClick={() => toggleLevelFilter('warning')}
                >
                  {levelCounts.warning} warnings
                </Badge>
              )}
            </div>
          </div>

          {/* Search and Filter Bar */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--color-muted-foreground)]" />
              <Input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search breadcrumbs..."
                className="pl-9 h-8 text-sm"
              />
            </div>
            <Button
              variant={showFilters ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="h-8"
            >
              <Filter className="h-3.5 w-3.5 mr-1" />
              Filters
              {hasActiveFilters && (
                <span className="ml-1 rounded-full bg-[var(--color-primary)] text-[var(--color-primary-foreground)] text-[10px] w-4 h-4 flex items-center justify-center">
                  {levelFilter.length + typeFilter.length + (searchTerm ? 1 : 0)}
                </span>
              )}
            </Button>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="h-8 px-2"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-3 space-y-3">
              {/* Type filters */}
              <div>
                <label className="text-xs font-medium text-[var(--color-muted-foreground)] mb-1.5 block">
                  Type
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {uniqueTypes.map(type => (
                    <button
                      key={type}
                      onClick={() => toggleTypeFilter(type)}
                      className={cn(
                        'px-2 py-1 rounded text-xs border transition-colors',
                        typeFilter.includes(type)
                          ? 'bg-[var(--color-primary)] text-[var(--color-primary-foreground)] border-[var(--color-primary)]'
                          : 'bg-[var(--color-background)] border-[var(--color-border)] hover:bg-[var(--color-muted)]'
                      )}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              {/* Level filters */}
              <div>
                <label className="text-xs font-medium text-[var(--color-muted-foreground)] mb-1.5 block">
                  Level
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {uniqueLevels.map(level => {
                    const classes = getLevelClasses(level);
                    return (
                      <button
                        key={level}
                        onClick={() => toggleLevelFilter(level)}
                        className={cn(
                          'px-2 py-1 rounded text-xs border transition-colors',
                          levelFilter.includes(level)
                            ? cn(classes.bg, classes.border, classes.text, 'ring-2', classes.border.replace('border-', 'ring-'))
                            : 'bg-[var(--color-background)] border-[var(--color-border)] hover:bg-[var(--color-muted)]'
                        )}
                      >
                        {level}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Timeline */}
          <div className="max-h-96 overflow-y-auto pr-2">
            {filteredBreadcrumbsWithRaw.length > 0 ? (
              filteredBreadcrumbsWithRaw.map(({ normalized, raw }, index) => (
                <BreadcrumbItem
                  key={`${normalized.timestamp}-${index}`}
                  breadcrumb={normalized}
                  rawBreadcrumb={raw}
                  isLast={index === filteredBreadcrumbsWithRaw.length - 1}
                  searchTerm={searchTerm}
                />
              ))
            ) : (
              <div className="text-center py-8 text-[var(--color-muted-foreground)]">
                No breadcrumbs match your filters
              </div>
            )}
          </div>
        </div>
      )}

      {/* Backend error details (e.g., Prisma errors) */}
      {context.error && context.success === false && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-red-500" />
            Error Details
            {context.errorCode && (
              <Badge variant="error" className="ml-2">{context.errorCode}</Badge>
            )}
          </h4>
          {(context.model || context.action) && (
            <div className="flex flex-wrap gap-2 text-xs">
              {context.model && (
                <span className="px-2 py-1 rounded bg-[var(--color-muted)] text-[var(--color-muted-foreground)]">
                  Model: <strong>{context.model}</strong>
                </span>
              )}
              {context.action && (
                <span className="px-2 py-1 rounded bg-[var(--color-muted)] text-[var(--color-muted-foreground)]">
                  Action: <strong>{context.action}</strong>
                </span>
              )}
              {context.duration !== undefined && (
                <span className="px-2 py-1 rounded bg-[var(--color-muted)] text-[var(--color-muted-foreground)]">
                  Duration: <strong>{context.duration}ms</strong>
                </span>
              )}
            </div>
          )}
          <CodeBlock className="text-red-600 dark:text-red-400 whitespace-pre-wrap">{context.error}</CodeBlock>
        </div>
      )}

      {/* Stack trace if present (check both stack and errorStack fields) */}
      {(context.stack || context.errorStack) && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-red-500" />
            Stack Trace
          </h4>
          <CodeBlock maxHeight="max-h-48" className="text-red-600 dark:text-red-400">{context.stack || context.errorStack || ''}</CodeBlock>
        </div>
      )}
    </div>
  );
}

export default ErrorContextViewer;
